import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { httpServices } from '@/services/axiosClient';
import { useAppContext } from '@/context';
import { AppConstants, COOKIE_MAX_AGE } from '@/utils/constants';
import { getUserInfo } from '@/services/api/coreService';
import { useAuth0Token } from './useAuth0Token';
import { jwtDecode } from 'jwt-decode';
import { notification } from 'antd';

export function useAppInit() {
  const [isLoading, setIsLoading] = useState(true);
  const { setUser } = useAppContext();
  const {
    isAuthenticated,
    user: auth0User,
    tokenReady,
    getAccessTokenSilently,
  } = useAuth0Token();
  const { isLoading: auth0Loading } = useAuth0();

  useEffect(() => {
    const assignToken = async () => {
      if (auth0Loading) {
        return;
      }

      const getTokenFromUrl = () => {
        const hash = window.location.hash;
        const search = window.location.search;

        if (hash) {
          const hashParams = new URLSearchParams(hash.substring(1));
          return hashParams.get('access_token') || hashParams.get('token');
        }

        if (search) {
          const searchParams = new URLSearchParams(search);
          return searchParams.get('access_token') || searchParams.get('token');
        }

        return null;
      };

      const clearUrlToken = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        url.searchParams.delete('access_token');
        if (url.hash.includes('token=') || url.hash.includes('access_token=')) {
          url.hash = '';
        }
        window.history.replaceState({}, document.title, url.toString());
      };

      const finalizeInitFallback = async (token: string, urlToken: string | null, additionalData: any = {}) => {
        const baseUser = {
          auth0_user_id: auth0User?.sub,
          ...auth0User,
          ...additionalData,
        };
        setUser(baseUser as any);
        httpServices.llmService.updateToken(token);
        httpServices.saasService.updateToken(token);
        try {
          const userInfo = await getUserInfo();
          if (userInfo) {
            setUser({ ...baseUser, ...userInfo } as any);
          }
        } catch (e) {
          console.warn('Failed to fetch user info during init fallback', e);
        }
        if (urlToken) {
          clearUrlToken();
        } else {
          localStorage.removeItem('access_token');
        }
        setIsLoading(false);
      };

      const urlToken = getTokenFromUrl();
      const storedToken = localStorage.getItem('access_token');

      if (urlToken) {
        httpServices.llmService.updateToken(urlToken);
        httpServices.saasService.updateToken(urlToken);
        localStorage.setItem('access_token', urlToken);
      }

      // If there is a stored SaaS token but no Auth0 session and no URL token,
      // still call getUserInfo() to rehydrate user state (e.g. is_admin) on page reload.
      if (!isAuthenticated && !urlToken) {
        if (storedToken) {
          try {
            const userInfo = await getUserInfo();
            if (userInfo) {
              setUser({ ...userInfo } as any);
            }
          } catch (e) {
            console.warn('Failed to rehydrate user info from stored token', e);
          }
        }
        setIsLoading(false);
        return;
      }

      if (!tokenReady && !urlToken) {
        setIsLoading(false);
        return;
      }

      try {
        let token = urlToken || (await getAccessTokenSilently());

        if (!token || typeof token !== 'string' || token.trim().length === 0) {
          setIsLoading(false);
          return;
        }

        if (!token.includes('.') || token.split('.').length < 3) {
          return finalizeInitFallback(token, urlToken);
        }

        let payload: any;
        try {
          payload = jwtDecode(token);
        } catch (parseError) {
          return finalizeInitFallback(token, urlToken);
        }

        if (!payload || typeof payload !== 'object') {
          return finalizeInitFallback(token, urlToken);
        }

        const userData = {
          ...payload,
          ...auth0User,
          auth0_user_id: auth0User?.sub,
        };

        setUser(userData);
        httpServices.llmService.updateToken(token);
        httpServices.saasService.updateToken(token);

        if (!urlToken) {
          localStorage.removeItem('access_token');
        }

        try {
          const userInfo = await getUserInfo();
          if (userInfo) {
            setUser({ ...userData, ...userInfo });
          }
        } catch (apiError) {
          console.warn('Failed to fetch user info during init fallback', apiError);
        }

        if (urlToken) {
          clearUrlToken();
        }
        setIsLoading(false);

      } catch (error: any) {
        const is401 = error?.response?.status === 401;

        if (is401) {
          // Remove the invalid token so the user isn't stuck in a broken state
          localStorage.removeItem('access_token');
          httpServices.llmService.updateToken('');
          httpServices.saasService.updateToken('');

          notification.error({
            message: 'Token inválido',
            description: 'O token de acesso fornecido é inválido ou está expirado. Por favor, faça login novamente.',
            duration: 6,
          });
        } else if (window.self !== window.top) {
          window?.parent?.postMessage(
            { type: AppConstants.reloadMessage },
            AppConstants.SAAS_DOMAIN,
          );
        }

        setIsLoading(false);
      }
    };

    assignToken();
  }, [
    isAuthenticated,
    tokenReady,
    auth0Loading,
    auth0User,
    getAccessTokenSilently,
    setUser,
  ]);

  return {
    isLoading: isLoading || auth0Loading,
  };
}
